import cx from 'classnames';
import Image from 'next/image';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { Ratio, RATIO } from '../../../../../../design-system/components/ratio/Ratio';
import { Carousel } from '../../../../../../domains/Layout/Carousel/Carousel';
import { imageLoaderForId } from '../../../../../../domains/Layout/Image/URL/ImageURL';
import { Agency } from '../../../../../../resources/agency/domain/types/Agency';
import { LocalHeroExplanation } from '../../../../../../shared/components/LocalHero/Explanation/LocalHeroExplanation';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';

import style from './MembersOfAgency.module.css';

type MembersOfAgencyProps = {
    agency: PickDomain<
        Agency,
        'name' | 'isLocalHero' | 'id' | 'slug' | 'betterTripIndex',
        {
            presentationVideoEmbedUrl: string | null;
            team: Array<{
                name: string;
                pictureId: number | null;
            }>;
        }
    >;
};

export const MembersOfAgency = ({ agency }: MembersOfAgencyProps) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const handleClickAgentCard = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.agentClicked({
                ...defaultProperties,
            });
        });
    };

    return (
        <section className={cx(style.sectionAgency, 'layout-with-centered-content')}>
            <div className="main-container">
                {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                <h2 className="font-m-extrabold">
                    {formatMessage({
                        id: 'page.agency.members',
                    })}
                </h2>
                {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                <p className={cx('font-body-bold', style.subtitle)}>
                    {formatMessage(
                        {
                            id: 'page.agency.members-subtitle',
                        },
                        {
                            agencyName: agency.name,
                            agencyMembersCount: agency.team.length,
                        },
                    )}
                </p>
                {agency.presentationVideoEmbedUrl ? (
                    <div className={style.subsection}>
                        <iframe
                            width="100%"
                            height="727"
                            src={agency.presentationVideoEmbedUrl}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            rel="0"
                            title={formatMessage({
                                id: 'page.agency.presentationVideo.iframe.title',
                            })}
                        />
                    </div>
                ) : null}
                <div className={style.subsection}>
                    <Carousel
                        slidesVisibleRules={{ sm: 2, md: 3, lg: 4, xl: 4, xxl: 4 }}
                        trackingInformation={{
                            navClick: {
                                block: 'agent_list',
                            },
                        }}
                    >
                        {agency.team.map(({ name, pictureId }) => {
                            return (
                                <div
                                    key={name}
                                    className={style.agencyMemberWrapper}
                                    onClick={() => handleClickAgentCard()}
                                >
                                    <Ratio ratio={RATIO['3/4']} className={style.imageRatio}>
                                        {pictureId ? (
                                            <Image
                                                alt={name}
                                                src={pictureId.toString()}
                                                loader={imageLoaderForId({
                                                    aspectRatio: RATIO['3/4'],
                                                })}
                                                sizes="300px"
                                                fill
                                                priority={false}
                                                className={style.image}
                                            />
                                        ) : null}
                                    </Ratio>
                                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                                    <p className="font-body-bold">{name}</p>
                                </div>
                            );
                        })}
                    </Carousel>
                </div>
                {agency.isLocalHero ? (
                    <div className={style.subsection}>
                        <LocalHeroExplanation />
                    </div>
                ) : null}
            </div>
        </section>
    );
};
